import {CONFIG} from '../../appConfig';
import {STATE} from '../../constants';

const RESERVATIONS = 'reservations/';

export default {

  /**
   * @desc Set CDA session with country, city, lat and long location information
   * @param {Object} data
   * @returns {Object}
   * @memberof services.cda.reservations
   * @example
   * const payload = {
   *  country_code, // *(string)* i.e. "CN"
   *  city_name, // *(string)* i.e. "Beijing"
   *  latitude, // *(number)* i.e. 39.8537385
   *  longitude  // *(number)* i.e. 116.4267957
   * };
   * services.cda.reservations.citySelect({payload});
   * // dispatched results stored in `state.gmi.cda.session.current
   */
  citySelect(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}citySelect`,
      data: payload,
      params
    };
  },

  /**
   * @desc CDA supported airlines request
   * @param {Object} data
   * @returns {Object}
   * @memberof services.cda.reservations
   */
  getAirlineList(data = {}) {
    const {params = {}} = data;
    return {
      method: 'GET',
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: 'getAirlineList',
      params,
      statePath: STATE.CDA.RESERVATION.AIRLINE_LIST,
      trimRespProp: 'airlines'
    };
  },

  /**
   * @desc CDA reservation booking_type selection
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.cda.reservations
   * @example
   * const payload = {
   *   booking_type // *(String)* i.e. "POINT_TO_POINT"
   * };
   * services.cda.reservations.saveBookingType({payload});
   * // dispatched results stored in `state.gmi.cda.session.current`
   */
  saveBookingType(data = {}) {
    const {payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: `saveBookingType`,
      data: payload,
      params
    };
  },

  /**
   * @desc CDA reservation initiation
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.cda.reservations
   * @example
   * const payload = {
   *   pickup, // *(Object)* i.e. {location}
   *   dropoff, // *(Object)* i.e. as above or {location, as_directed}
   *   requested_duration, // *(Number)*
   *   number_of_passengers, // *(Number)*,
   *   extra_stops // *(Array)* Optional. Array of location objects
   * };
   * services.cda.reservations.initiate({payload});
   * // dispatched results stored in `state.gmi.cda.session.current`
   */
  initiate(data = {}) {
    const {payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}initiate`,
      data: payload,
      params
    };
  },

  /**
   * @desc CDA reservation carSelect
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.cda.reservations
   * @example
   * const payload = {
   *   provider_search_id // *(String)* i.e. "4621-3E281D0-1076-0036-01"
   * };
   * services.cda.reservations.carSelect({payload});
   * // dispatched results stored in `state.gmi.cda.session.current`
   */
  carSelect(data = {}) {
    const {payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}carSelect`,
      data: payload,
      params
    };
  },

  /**
   * @desc CDA reservation travelerInformation
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.cda.reservations
   * @example
   * const payload = {
   *   passenger: {
   *     first_name, // *(String)* i.e. "Bob",
   *     last_name, // *(String)* i.e. "Costas",
   *     day_phone, // *(String)* i.e. "555-555-5555",
   *     cell_phone, // *(String)* i.e. "555-555-5555",
   *     email_address // *(String)* i.e. "bob.costas@nbc.com"
   *   },
   *   booker: {
   *     ...copy of passenger
   *   }
   * };
   * services.cda.reservations.travelerInformation({payload});
   * // dispatched results stored in `state.gmi.cda.session.current`
   */
  travelerInformation(data = {}) {
    const {payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}travelerInformation`,
      data: payload,
      params
    };
  },

  /**
   * @desc CDA get url for payment iframe
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.cda.reservations
   * @example
   * const payload = {
   *   cssUrl: `${window.location.origin}${GLOBAL.ASSETS_PATH}${NCD_PAYMENT_CSS_PATH}`
   * };
   * services.cda.reservations.getPaymentUrl({payload});
   */
  getPaymentUrl(data = {}) {
    const {payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: `getPaymentUrl`,
      data: payload,
      params
    };
  },

  /**
   * @desc CDA save payment data to session
   * @param {Object} data {payload}
   * @returns {Object}
   * @memberof services.cda.reservations
   * @example
   * const payload = {
   *   payment_info: {
   *     token: `determined by DEEM`,
   *     cc_last_four: '1234'
   *     cc_type: 'Visa'
   *   }
   * };
   * services.cda.reservations.savePaymentData({payload});
   * // dispatched results stored in `state.gmi.cda.session.current`
   */
  savePaymentData(data = {}) {
    const {payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: `savePaymentData`,
      data: payload,
      params
    };
  },

  /**
   * @desc CDA create reservation from session
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.cda.reservations
   * @example
   * services.cda.reservations.create();
   */
  create(data = {}) {
    const {params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}create`,
      params
    };
  },

  /**
   * @desc CDA cancel booking
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.cda.reservations
   * @example
   * const payload = {
   *   confirmation_number: `confirmation number from reservation`
   * };
   * services.cda.reservations.cancel({payload});
   */
  cancel(data ={}) {
    const {payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}cancel`,
      data: payload,
      params
    };
  },

  /**
   * @desc CDA get booking
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.cda.reservations
   * @example
   * const payload = {
   *   confirmation_number: `confirmation number from reservation`
   * };
   * services.cda.reservations.retrieve({payload});
   */
  retrieve(data ={}) {
    const {payload = {}, params = {}} = data;
    return {
      method: 'POST',
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}retrieve`,
      data: payload,
      params
    };
  },

  /**
   * @desc CDA restart booking
   * @param {Object} data {params}
   * @returns {Object}
   * @memberof services.cda.reservations
   * @example
   * services.cda.reservations.restart();
   */
  restart(data = {}) {
    const { params = {} } = data;
    return {
      method: "POST",
      origin: CONFIG.CDA_ORIGIN_URL,
      endpoint: `${RESERVATIONS}restart`,
      params
    };
  }
};
